import { createTheme, CSSVariablesResolver, MantineColorsTuple } from "@mantine/core";

// Built around primary #FF7043 at shade 6, accent #FF5722 at 7, secondary #FFAB91 at 3, warning #FFB74D at 2.
const orange: MantineColorsTuple = [
  "#FFF3E0", // 0 – lightest tint
  "#FFE0B2", // 1
  "#FFB74D", // 2 – warning
  "#FFAB91", // 3 – secondary
  "#FF8A65", // 4
  "#FF7B55", // 5
  "#FF7043", // 6 – primary  ★
  "#FF5722", // 7 – accent / hover
  "#A63A15", // 8
  "#863010", // 9 – deepest
];

// Built around the base/neutral greys from the design tokens.
const grey: MantineColorsTuple = [
  "#FAFAFA", // 0
  "#F5F5F5", // 1
  "#E0E0E0", // 2 – base-content (primary text)
  "#BDBDBD", // 3
  "#9E9E9E", // 4 – neutral-content / info / secondary text
  "#757575", // 5
  "#616161", // 6
  "#424242", // 7
  "#2C2C2C", // 8 – base-300 / borders / neutral
  "#1E1E1E", // 9 – base-200 / surface / cards
];

// The flag font is first because it has to be: a system font that draws a flag as the letter pair it is built from has
// covered those codepoints, so nothing further down the stack is ever reached. It holds flags and nothing else, and
// global.css scopes it to their codepoints, so it cannot touch a glyph anywhere else on the site.
// "Roboto Variable" is the family @fontsource-variable/roboto declares; plain Roboto covers a locally installed copy.
const ROBOTO = "'Twemoji Country Flags', 'Roboto Variable', Roboto, -apple-system, BlinkMacSystemFont, Segoe UI, "
  + "Helvetica, Arial, sans-serif";

export const theme = createTheme({
  colors: { orange, grey },
  primaryColor: "orange",
  primaryShade: 9,

  fontFamily: ROBOTO,
  headings: { fontFamily: ROBOTO },

  defaultRadius: "md",

  other: {
    /* Expose the raw design tokens so components can reference them */
    base100: "#121212",
    base200: "#1E1E1E",
    base300: "#2C2C2C",
    baseContent: "#E0E0E0",
    primary: "#FF7043",
    primaryContent: "#121212",
    secondary: "#FFAB91",
    secondaryContent: "#121212",
    neutral: "#2C2C2C",
    neutralContent: "#9E9E9E",
    accent: "#FF5722",
    accentContent: "#121212",
    info: "#9E9E9E",
    infoContent: "#121212",
    success: "#FF7043",
    successContent: "#121212",
    warning: "#FFB74D",
    warningContent: "#121212",
    error: "#FFB4AB",
    errorContent: "#690005",
  },
});

// The same dark orange-grey palette in both schemes, so the look is identical whichever one is toggled on.
export const cssVariablesResolver: CSSVariablesResolver = () => {
  const shared: Record<string, string> = {
    "--mantine-color-body": "#121212",
    "--mantine-color-text": "#E0E0E0",
    "--mantine-color-dimmed": "#9E9E9E",
    "--mantine-color-default": "#1E1E1E",
    "--mantine-color-default-hover": "#2C2C2C",
    "--mantine-color-default-color": "#E0E0E0",
    "--mantine-color-default-border": "#2C2C2C",

    /* Error colors from the design tokens */
    "--mantine-color-error": "#FFB4AB",
  };

  return {
    variables: {},
    light: { ...shared },
    dark: { ...shared },
  };
};
